/*******************************************************************************
  DownloadLocationData.js

  Author: Advait; 

*******************************************************************************/
// ensure a filename does not have invalid characters
import sanitize from "sanitize-filename";

// Constants
const DEFAULT_FONT_FAMILY =
  '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans","Liberation Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"';

/**
 * Generate a filesystem-safe .jpg filename from a name and time.
 * "<sanitizedName>-<sanitizedTime>.jpg".
 */
const sanitizeFilename = (name) => {
  // Remove spaces and illegal characters from the file name
  name = name.replace(/ /g, "_");
  name = sanitize(name);
  return `${name}.jpg`;
};

const triggerDownloadBlob = (blob, filename) => {
  if (!blob || !filename) return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const downloadCanvas = (canvas, filename, mime = "image/jpeg", quality = 1.0) => {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob && blob.size > 0) triggerDownloadBlob(blob, filename);
        resolve();
      },
      mime,
      quality
    );
  });
};

export default async function downloadLocationData(currentLocation, sitename) {
  const imgSrc = currentLocation?.url;

  const resp = await fetch(imgSrc, { mode: "cors" });
  if (!resp.ok) throw new Error("Failed to fetch image: " + resp.status);
  const blob = await resp.blob();

  const bitmap = await createImageBitmap(blob);

  const canvas = document.createElement("canvas");
  canvas.width = 1920;
  canvas.height = 1080 + 25; // Add 25px for the reserved top area for overlay text
  const ctx = canvas.getContext("2d");

  // Fill background to match VisualTour topbar look
  ctx.fillStyle = "rgba(51,56,62,1)";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw the image scaled to the target area
  ctx.drawImage(bitmap, 0, 25, 1920, 1080);

  await document.fonts?.ready;

  // Draw overlay in the reserved top area
  let fontSize = 14;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff";

  const text = `Site: ${String(sitename)} \u2003 Location: ${String(
    currentLocation.name
  )} \u2003 Time: ${String(currentLocation.time)}`;

  // Use only the DEFAULT_FONT_FAMILY
  ctx.font = `${fontSize}px ${DEFAULT_FONT_FAMILY}`;

  const textY = Math.floor(25 / 2);
  ctx.fillText(text, Math.floor(canvas.width / 2), textY);

  // Download the generated image
  await downloadCanvas(
    canvas,
    sanitizeFilename(`${sitename}-${currentLocation.name}-${currentLocation.time}`)
  );
}
